import {FC, useEffect, useState} from 'react'
import {Modal, Spinner} from 'react-bootstrap'
import InputAutomation from './modal/InputAutomation'
import InputPipeline from './modal/InputPipeline'
import {
  Automation,
  AutomationPipelineType,
  AutomationRule,
} from '../../../../../services/cellcast.v2.types'
import {
  useCreateMultiStepAutomationMutation,
  useLazyGetDetailMultiStepAutomationQuery,
  useUpdateMultiStepAutomationMutation,
} from '../../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import { useLazyGetAllTagsQuery } from '../../../../../services/cellcast'
import { SelectOption } from '../../../../types'

export enum WriteMultiStepType {
  InputAutomation = 'inputAutomation',
  InputPipeline = 'inputPipeline',
}

export type InputErrors = {
  message: string
  fields: Record<string, any>
}

type WriteMultiStepProps = {
  show: boolean
  id: string
  closeModal: (isReload: boolean) => void
}

const defaultAutomationData: Automation = {
  trigger: '',
  rule: AutomationRule.anyMessage,
  status: true,
  title: '',
  searchTerm: '',
  type: 'multi',
  pipelines: [
    {
      type: AutomationPipelineType.addTag,
      config: {},
    },
  ],
}

const WriteMultiStep: FC<WriteMultiStepProps> = (props) => {
  const {id, show, closeModal} = props

  // local state
  const [mode, setMode] = useState('create')
  const [currentStep, setCurrentStep] = useState(WriteMultiStepType.InputAutomation)
  const [data, setData] = useState<Automation>(defaultAutomationData)
  const [doWrite, setDoWrite] = useState(false)
  const [error, setError] = useState<string>('')
  const [tagOptions, setTagOptions] = useState<SelectOption<string>[]>([])

  // rtk query
  const [create, {isLoading: createLoading}] = useCreateMultiStepAutomationMutation()
  const [update, {isLoading: updateLoading}] = useUpdateMultiStepAutomationMutation()
  const [getDetail, {isLoading: getDetailLoading, isFetching: getDetailFetching}] =
    useLazyGetDetailMultiStepAutomationQuery()
  const [getTag, {data: tagResponse}] = useLazyGetAllTagsQuery()

  // actions
  const handleClose = () => {
    setCurrentStep(WriteMultiStepType.InputAutomation)
    setError('')
    closeModal(false)
  }

  const handleInputAutomationNext = () => {
    setCurrentStep(WriteMultiStepType.InputPipeline)
  }

  const performWrite = (payload: Automation) => {
    let actionFn = create
    if (mode === 'update') {
      actionFn = update
    }

    if (mode === 'update' && !payload._id) {
      return
    }

    actionFn({...payload})
      .then((res) => {
        if ('data' in res) {
          if (res.data?.error && Object.keys(res.data?.error).length > 0) {
            setError(res?.data?.message)
          } else {
            toast.success(res?.data?.message)
            setData(defaultAutomationData)
            setError('')
            setCurrentStep(WriteMultiStepType.InputAutomation)
            closeModal(true)
          }
        } else if ('error' in res) {
          toast.error(res.error)
          setError(`${error}`)
        }
      })
      .finally(() => {
        setDoWrite(false)
      })
  }

  const handleInputPipelineNext = () => {
    setDoWrite(true)
  }

  const handleInputPipelineBack = () => {
    setCurrentStep(WriteMultiStepType.InputAutomation)
  }

  // effect
  useEffect(() => {
    if (show === true) {
      if (id && id.length > 0) {
        setMode('update')
        setCurrentStep(WriteMultiStepType.InputAutomation)
        getDetail(id).then((res) => {
          if ('data' in res) {
            if (res.data?.error && Object.keys(res.data?.error).length > 0) {
              toast.error(res?.data?.message)
            } else if (res.data?.data) {
              setError('')
              setData(res.data.data)
            }
          } else if ('error' in res) {
            toast.error(res.error)
          }
        })
      } else {
        setCurrentStep(WriteMultiStepType.InputAutomation)
        setMode('create')
        setData(defaultAutomationData)
      }
      getTag(null)
    }
  }, [id, show])

  useEffect(() => {
    if (doWrite === true) {
      performWrite(data)
    }
  }, [doWrite])

  useEffect(() => {
    if (tagResponse && tagResponse.data && Array.isArray(tagResponse.data)) {
      const allTags = tagResponse.data.filter((el) => el.type && el.type !== 'list')
      const allOptions = allTags.map((el) => ({label: el.name, value: el._id}))
      setTagOptions(allOptions)
    }
  }, [tagResponse])

  return (
    <Modal
      id='kt_modal_create_app'
      tabIndex={1}
      aria-hidden='true'
      dialogClassName='modal-dialog-centered mw-700px'
      className='vh-100'
      show={show}
      size='lg'
      backdrop={false}
      onHide={() => closeModal(false)}
    >
      {getDetailLoading || getDetailFetching ? (
        <div className='d-flex justify-content-center align-items-center' style={{height: 200}}>
          <Spinner animation='grow' />
        </div>
      ) : (
        <>
          {currentStep === WriteMultiStepType.InputAutomation && (
            <InputAutomation
              handleClose={handleClose}
              error={error}
              data={data}
              tagOptions={tagOptions}
              setData={setData}
              handleNext={handleInputAutomationNext}
            />
          )}
          {currentStep === WriteMultiStepType.InputPipeline && (
            <InputPipeline
              mode={mode}
              currentStep={currentStep}
              isLoading={createLoading || updateLoading}
              handleClose={handleClose}
              error={error}
              data={data}
              tagOptions={tagOptions}
              setData={setData}
              handleBack={handleInputPipelineBack}
              handleNext={handleInputPipelineNext}
            />
          )}
        </>
      )}
    </Modal>
  )
}

export default WriteMultiStep
